import React from 'react';
import { Clock, MapPin, Bell } from 'lucide-react';
import { Drop } from '../types';
interface DropCardProps {
  drop: Drop;
  onClick: () => void;
  onSetReminder?: () => void;
}
export function DropCard({
  drop,
  onClick,
  onSetReminder
}: DropCardProps) {
  const getTimeRemaining = () => {
    const end = new Date(drop.endTime);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };
  const getStartTime = () => {
    const start = new Date(drop.startTime);
    return start.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  const getStatusBadge = () => {
    if (drop.status === 'live') {
      return <div className="flex items-center gap-1.5 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold" data-id="element-50">
          <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" data-id="element-51" />
          LIVE
        </div>;
    }
    if (drop.status === 'upcoming') {
      return <div className="flex items-center gap-1.5 bg-gray-600 text-white px-3 py-1 rounded-full text-xs font-bold" data-id="element-52">
          <Clock size={12} data-id="element-53" />
          UPCOMING
        </div>;
    }
    return null;
  };
  const handleReminderClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSetReminder?.();
  };
  return <button onClick={onClick} className="w-full bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all active:scale-[0.98]" data-id="element-54">
      {/* Brand Header */}
      <div className="p-4 pb-3 flex items-center justify-between border-b border-gray-100" data-id="element-55">
        <div className="flex items-center gap-3" data-id="element-56">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden" data-id="element-57">
            <img src={drop.brandLogo} alt={drop.brandName} className="w-full h-full object-cover" data-id="element-58" />
          </div>
          <div className="text-left" data-id="element-59">
            <h3 className="font-bold text-sm text-gray-900" data-id="element-60">
              {drop.brandName}
            </h3>
            <p className="text-xs text-gray-500" data-id="element-61">{drop.category}</p>
          </div>
        </div>
        {getStatusBadge()}
      </div>

      {/* Item Image */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-gray-50 to-gray-100" data-id="element-62">
        <img src={drop.itemImage} alt={drop.itemName} className="w-full h-full object-cover" data-id="element-63" />
        {drop.status === 'live' && <div className="absolute top-3 left-3 bg-black/80 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5" data-id="element-64">
            <Clock size={12} data-id="element-65" />
            Ends in {getTimeRemaining()}
          </div>}
        {drop.status === 'upcoming' && <div className="absolute top-3 left-3 bg-black/80 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5" data-id="element-66">
            <Clock size={12} data-id="element-67" />
            Starts {getStartTime()}
          </div>}
      </div>

      {/* Item Details */}
      <div className="p-4 text-left" data-id="element-68">
        <h2 className="font-bold text-lg text-gray-900 mb-1" data-id="element-69">
          {drop.itemName}
        </h2>
        <p className="text-sm text-gray-600 mb-3 line-clamp-2" data-id="element-70">
          {drop.description}
        </p>

        <div className="flex items-center justify-between" data-id="element-71">
          <div className="flex items-center gap-4" data-id="element-72">
            <div data-id="element-73">
              <p className="text-xs text-gray-500" data-id="element-74">Value</p>
              <p className="font-bold text-purple-600" data-id="element-75">₹{drop.value}</p>
            </div>
            <div className="w-px h-8 bg-gray-200" data-id="element-76" />
            <div className="flex items-center gap-1.5 text-gray-600" data-id="element-77">
              <MapPin size={14} data-id="element-78" />
              <span className="text-sm font-medium" data-id="element-79">
                {drop.storesNearby} nearby
              </span>
            </div>
          </div>

          {drop.status === 'live' && <div className="bg-[#FFFC00] text-black px-4 py-2 rounded-lg font-bold text-sm" data-id="element-80">
              Unlock →
            </div>}
          {drop.status === 'upcoming' && <button onClick={handleReminderClick} className="bg-purple-100 text-purple-700 px-4 py-2 rounded-lg font-medium text-sm hover:bg-purple-200 transition-colors flex items-center gap-1.5" data-id="element-81">
              <Bell size={14} data-id="element-82" />
              Remind Me
            </button>}
        </div>
      </div>
    </button>;
}